import type { Equation, EquationDiagnostic, MatrixAnalysis } from "./equation";
import type { Dimension, MatrixFor, VectorFor } from "@src/math/types";
import { canAddEquationNodes } from "./equation-operations";

export type PresetCapability<T> =
  | { readonly kind: "available"; readonly value: T }
  | { readonly kind: "invalid-matrices" }
  | {
      readonly kind: "unavailable";
      readonly reason:
        | "complex-eigenvalues"
        | "numerical-failure"
        | "singular-transform";
    }
  | { readonly kind: "capacity-exceeded" };

export interface EquationCapabilities<D extends Dimension> {
  readonly inverseTransform: PresetCapability<Readonly<MatrixFor<D>>>;
  readonly eigenbasis: PresetCapability<readonly Readonly<VectorFor<D>>[]>;
  readonly eigenvector: PresetCapability<Readonly<VectorFor<D>>>;
}

export function analyzeEquationCapabilities<D extends Dimension>(
  equation: Equation<D>,
): EquationCapabilities<D> {
  const matricesValid = areEquationMatricesValid(equation);
  const analysis = matrixAnalysis(equation);
  return !matricesValid || analysis === null
    ? {
        inverseTransform: { kind: "invalid-matrices" },
        eigenbasis: { kind: "invalid-matrices" },
        eigenvector: { kind: "invalid-matrices" },
      }
    : {
        inverseTransform: inverseTransformCapability(equation, analysis),
        eigenbasis: eigenbasisCapability(equation, analysis),
        eigenvector: eigenvectorCapability(equation, analysis),
      };
}

function inverseTransformCapability<D extends Dimension>(
  equation: Equation<D>,
  analysis: MatrixAnalysis<D>,
): PresetCapability<Readonly<MatrixFor<D>>> {
  if (analysis.inverseTransform.kind === "singular")
    return { kind: "unavailable", reason: "singular-transform" };
  return canAddEquationNodes(equation, "matrices")
    ? { kind: "available", value: analysis.inverseTransform.values }
    : { kind: "capacity-exceeded" };
}

export function areEquationMatricesValid<D extends Dimension>(
  equation: Equation<D>,
): boolean {
  if (matrixAnalysis(equation) === null) return false;
  const matrixIds = new Set(equation.matrices.map(({ id }) => id));
  return !getEquationDiagnostics(equation).some(
    (diagnostic) =>
      diagnostic.location === undefined ||
      matrixIds.has(diagnostic.location.nodeId),
  );
}

function matrixAnalysis<D extends Dimension>(
  equation: Equation<D>,
): MatrixAnalysis<D> | null {
  return equation.evaluation.kind === "current"
    ? equation.evaluation.matrixAnalysis
    : equation.evaluation.kind === "stale"
      ? (equation.evaluation.checkpoint?.analysis ?? null)
      : null;
}

function eigenbasisCapability<D extends Dimension>(
  equation: Equation<D>,
  analysis: MatrixAnalysis<D>,
): PresetCapability<readonly Readonly<VectorFor<D>>[]> {
  const result = analysis.eigensystem;
  if (result.kind === "unavailable")
    return {
      kind: "unavailable",
      reason: "numerical-failure",
    };
  if (result.spectrum === "complex")
    return { kind: "unavailable", reason: "complex-eigenvalues" };
  if (result.realEigenvectors.length !== equation.dimension)
    return { kind: "unavailable", reason: "numerical-failure" };
  return canAddEquationNodes(
    equation,
    "vectors",
    result.realEigenvectors.length,
  )
    ? { kind: "available", value: result.realEigenvectors }
    : { kind: "capacity-exceeded" };
}

function eigenvectorCapability<D extends Dimension>(
  equation: Equation<D>,
  analysis: MatrixAnalysis<D>,
): PresetCapability<Readonly<VectorFor<D>>> {
  const result = analysis.eigensystem;
  if (result.kind === "unavailable")
    return {
      kind: "unavailable",
      reason: "numerical-failure",
    };
  const vector = result.realEigenvectors.at(0);
  if (!vector)
    return {
      kind: "unavailable",
      reason:
        result.spectrum === "complex"
          ? "complex-eigenvalues"
          : "numerical-failure",
    };
  return canAddEquationNodes(equation, "vectors")
    ? {
        kind: "available",
        value: vector,
      }
    : { kind: "capacity-exceeded" };
}

function getEquationDiagnostics<D extends Dimension>(
  equation: Equation<D>,
): readonly EquationDiagnostic[] {
  return equation.evaluation.kind === "stale"
    ? equation.evaluation.diagnostics
    : [];
}
